import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PackagePlus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", category: "", price: "", cost: "" };

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/catalog").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const categories = useMemo(() => ["All", ...new Set(items.map((i) => i.category))], [items]);
  const filtered = items.filter(
    (i) =>
      (category === "All" || i.category === category) &&
      i.name.toLowerCase().includes(query.toLowerCase())
  );

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, category: p.category, price: p.price, cost: p.cost ?? "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/catalog/${editing.id}`, { name: form.name, category: form.category, price: Number(form.price), cost: form.cost === "" ? null : Number(form.cost) });
        toast.success("Product updated");
      } else {
        await api.post("/catalog", { name: form.name, category: form.category, price: Number(form.price), ...(form.cost !== "" ? { cost: Number(form.cost) } : {}) });
        toast.success("Product added to catalog");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from the catalog?`)) return;
    await api.delete(`/catalog/${p.id}`);
    toast.success("Product deleted");
    load();
  };

  return (
    <div data-testid="catalog-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.4em] text-primary mb-2 flex items-center gap-3">
            <span className="h-px w-8 bg-primary/60" />
            PRICE LIST <span className="font-jp text-muted-foreground">部品カタログ</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight uppercase">Catalog</h1>
        </div>
        <Button
          data-testid="add-product-btn"
          onClick={openAdd}
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-widest rounded-sm h-11 px-5"
        >
          <PackagePlus size={14} className="mr-2" /> ADD PRODUCT
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="catalog-search-input"
            placeholder="Search catalog…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 bg-card border-border rounded-sm h-10 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="catalog-category-pills">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`catalog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setCategory(c)}
              className={`text-[10px] tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
                category === c
                  ? "bg-primary text-primary-foreground border-primary font-bold"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
              }`}
            >
              {c.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-md p-5" data-testid="catalog-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="catalog-table">
            <thead>
              <tr className="text-left text-muted-foreground text-[10px] tracking-[0.2em] border-b border-border">
                <th className="pb-2 font-medium">PART / SERVICE</th>
                <th className="pb-2 font-medium">CATEGORY</th>
                <th className="pb-2 font-medium text-right">SHOP COST</th>
                <th className="pb-2 font-medium text-right">PRICE</th>
                <th className="pb-2 font-medium text-right">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-border/50 hover:bg-secondary/30 transition-colors" data-testid={`catalog-row-${p.id}`}>
                  <td className="py-3 text-foreground font-semibold">{p.name}</td>
                  <td className="py-3">
                    <span className="text-[10px] tracking-widest text-primary/80 border border-primary/25 bg-primary/5 px-2 py-0.5 rounded-sm">
                      {p.category.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 text-right text-muted-foreground" data-testid={`catalog-cost-${p.id}`}>{p.cost != null ? fmt(p.cost) : "—"}</td>
                  <td className="py-3 text-right font-display font-bold text-foreground">{fmt(p.price)}</td>
                  <td className="py-3 text-right">
                    <button data-testid={`catalog-edit-${p.id}`} onClick={() => openEdit(p)} className="p-1.5 text-muted-foreground hover:text-primary transition-colors">
                      <Pencil size={13} />
                    </button>
                    <button data-testid={`catalog-delete-${p.id}`} onClick={() => remove(p)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-muted-foreground" data-testid="catalog-empty">
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0C100D] border-primary/30 text-foreground max-w-md" data-testid="product-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editing ? "EDIT PRODUCT" : "ADD PRODUCT"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="product-form">
            <div>
              <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">NAME</label>
              <Input data-testid="product-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">CATEGORY</label>
              <Input data-testid="product-category-input" required placeholder="e.g. Engine Swaps" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">PRICE ($)</label>
              <Input data-testid="product-price-input" type="number" min="0" step="1" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">SHOP COST $ (EMPLOYEE PRICE — OPTIONAL)</label>
              <Input data-testid="product-cost-input" type="number" min="0" step="1" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
            </div>
            <Button data-testid="product-save-btn" type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-[0.25em] h-12 rounded-sm">
              {busy ? "SAVING…" : editing ? "SAVE CHANGES" : "ADD TO CATALOG"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
